import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Upload, LogOut, ImagePlus, Loader2 } from "lucide-react";
import { LOGO, PHONE_DISPLAY } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "jh_admin_token";

export default function Dashboard() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setChecking(false); });
  }, [token]);

  const onLogin = (t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); };
  const onLogout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A1E3F]"><Loader2 className="h-10 w-10 text-white animate-spin" /></div>;
  }
  return token ? <Panel token={token} onLogout={onLogout} /> : <Login onLogin={onLogin} />;
}

function Login({ onLogin }) {
  const [pin, setPin] = useState("");
  const [last4, setLast4] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { pin, last4 });
      onLogin(data.token);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full h-14 px-4 border-2 border-[#0E2A56] bg-white text-[#14161A] font-medium text-lg tracking-widest text-center focus:outline-none focus:border-[#C8102E]";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1E3F] px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md bg-white border-4 border-white p-8 shadow-hard-red">
        <img src={LOGO} alt="Journey's Handyman" className="h-24 w-auto object-contain mx-auto mb-6" />
        <div className="flex items-center justify-center gap-2 text-[#0E2A56] mb-6">
          <Lock className="h-5 w-5 text-[#C8102E]" strokeWidth={2.5} />
          <h1 className="font-heading text-2xl font-extrabold uppercase tracking-tight">Owner Dashboard</h1>
        </div>
        <label className="block font-heading font-bold uppercase text-sm text-[#0E2A56] mb-1">PIN</label>
        <input data-testid="login-pin" className={inputCls} type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" />
        <label className="block font-heading font-bold uppercase text-sm text-[#0E2A56] mt-4 mb-1">Last 4 of Phone #</label>
        <input data-testid="login-last4" className={inputCls} inputMode="numeric" maxLength={4} value={last4} onChange={(e) => setLast4(e.target.value)} placeholder="0000" />
        <button data-testid="login-submit" disabled={busy} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C8102E] hover:bg-[#9E0C24] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wide text-lg py-4 transition-colors">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" strokeWidth={2.5} />}
          {busy ? "Signing in..." : "Sign In"}
        </button>
        <a href="/" className="mt-4 block text-center text-sm text-slate-500 hover:text-[#0E2A56]">← Back to website</a>
      </form>
    </div>
  );
}

function Panel({ token, onLogout }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState({ categories: [], items: [] });
  const [category, setCategory] = useState("painting");
  const [newCat, setNewCat] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [leads, setLeads] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/gallery`).then(({ data }) => setData(data)).catch(() => {});
    axios.get(`${API}/leads`, auth).then(({ data }) => setLeads(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : "");
  };

  const upload = async (e) => {
    e.preventDefault();
    const cat = (newCat.trim() || category).trim();
    if (!file) { toast.error("Choose a photo to upload"); return; }
    if (!cat) { toast.error("Pick or enter a category"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", cat);
    fd.append("caption", caption);
    try {
      await axios.post(`${API}/admin/gallery`, fd, auth);
      toast.success("Photo uploaded!");
      setFile(null); setPreview(""); setCaption(""); setNewCat("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };


  return (
    <div className="min-h-screen bg-[#F6F5F0]">
      <header className="bg-[#0E2A56] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Journey's Handyman" className="h-12 w-auto object-contain bg-white rounded p-1" />
            <span className="font-heading font-extrabold uppercase tracking-tight text-lg">Owner Dashboard</span>
          </div>
          <button data-testid="logout-button" onClick={onLogout} className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9E0C24] font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
            <LogOut className="h-4 w-4" strokeWidth={2.5} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-10">
        {/* Upload */}
        <section className="bg-white border-2 border-[#0E2A56] p-6">
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-[#0E2A56] flex items-center gap-2">
            <ImagePlus className="h-6 w-6 text-[#C8102E]" strokeWidth={2.5} /> Upload a Photo
          </h2>
          <form onSubmit={upload} className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className="block font-heading font-bold uppercase text-sm text-[#0E2A56] mb-1">Category</label>
              <select data-testid="upload-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-12 px-3 border-2 border-[#0E2A56] bg-white font-medium focus:outline-none focus:border-[#C8102E]">
                {data.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <input data-testid="upload-newcat" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="…or type a NEW category" className="mt-2 w-full h-11 px-3 border-2 border-dashed border-[#0E2A56]/50 bg-white font-medium focus:outline-none focus:border-[#C8102E]" />

              <label className="block font-heading font-bold uppercase text-sm text-[#0E2A56] mt-4 mb-1">Caption (optional)</label>
              <input data-testid="upload-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. New tile shower in Blairsville" className="w-full h-11 px-3 border-2 border-[#0E2A56] bg-white font-medium focus:outline-none focus:border-[#C8102E]" />
            </div>
            <div>
              <label className="block font-heading font-bold uppercase text-sm text-[#0E2A56] mb-1">Photo</label>
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-[#0E2A56] bg-[#F6F5F0] cursor-pointer hover:bg-white transition-colors overflow-hidden">
                {preview ? (
                  <img src={preview} alt="preview" className="h-full w-full object-cover" />
                ) : (
                  <><Upload className="h-8 w-8 text-[#0E2A56]" strokeWidth={2} /><span className="mt-2 text-sm text-slate-600 font-medium">Tap to choose a photo</span></>
                )}
                <input data-testid="upload-file" type="file" accept="image/*" onChange={pickFile} className="hidden" />
              </label>
              <button data-testid="upload-submit" disabled={busy} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#C8102E] hover:bg-[#9E0C24] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wide py-3 transition-colors">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" strokeWidth={2.5} />}
                {busy ? "Uploading..." : "Upload Photo"}
              </button>
            </div>
          </form>
        </section>

        {/* Leads */}
        <section className="bg-white border-2 border-[#0E2A56] p-6">
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-[#0E2A56] mb-4">
            Leads ({leads.length})
          </h2>
          {leads.length === 0 ? (
            <p className="text-slate-500">No leads yet. Form submissions and AI-chat captures will appear here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="text-left font-heading uppercase text-xs text-[#0E2A56] border-b-2 border-[#0E2A56]">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Phone</th>
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4">Details</th>
                    <th className="py-2 pr-4">When</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#0E2A56]/10 align-top">
                      <td className="py-2 pr-4 font-semibold">{l.name}</td>
                      <td className="py-2 pr-4"><a href={`tel:${l.phone}`} className="text-[#C8102E] font-semibold">{l.phone}</a></td>
                      <td className="py-2 pr-4"><span className={`px-2 py-0.5 text-xs font-heading uppercase ${l.source === "chat" ? "bg-[#0E2A56] text-white" : "bg-[#C8102E] text-white"}`}>{l.source || "form"}</span></td>
                      <td className="py-2 pr-4 text-slate-700 max-w-xs">{[l.service, l.message].filter(Boolean).join(" — ")}</td>
                      <td className="py-2 pr-4 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-center text-sm text-slate-500">Questions? Call {PHONE_DISPLAY}</p>
      </main>
    </div>
  );
}
